import type { IConnectOptions } from "@/types/auth.interface";

import { io, Socket } from "socket.io-client";
import { SOCKET_NAME_SPACES } from "./socket.enum";
import { clearAuthTokens, silentRefresh } from "@/utils/auth";
import { store } from "@/store";
import { setConnectionStatus } from "@/store/slices/connection-status.slice";
import { LOGOUT_USER_ACTION } from "@/utils/enums/auth.enums";
import toast from "react-hot-toast";

const BASE_URL = import.meta.env.VITE_WEB_SOCKET_BASE_URL;
type SocketEventHandler = (...args: unknown[]) => void;

class SocketManager {
  private sockets: Map<string, Socket> = new Map();
  private eventHandlers: Map<string, Map<string, Map<SocketEventHandler, SocketEventHandler>>> =
    new Map();
  // Tracks namespaces that already used their one silent-refresh recovery since
  // the last successful connect. Prevents an infinite refresh→reconnect loop.
  private authRecoveryAttempted: Set<string> = new Set();
  // Namespaces currently down. `reconnecting` = still retrying; `failed` =
  // socket.io gave up. Aggregated so multiple namespaces dropping surface ONE
  // app-wide status. Worst state wins: any failed → "disconnected".
  private reconnectingNamespaces: Set<string> = new Set();
  private failedNamespaces: Set<string> = new Set();

  // Recompute the single app-wide connection status from the down-sets.
  private syncConnectionStatus() {
    if (this.failedNamespaces.size > 0) {
      store.dispatch(setConnectionStatus("disconnected"));
    } else if (this.reconnectingNamespaces.size > 0) {
      store.dispatch(setConnectionStatus("reconnecting"));
    } else {
      store.dispatch(setConnectionStatus("connected"));
    }
  }

  private getNamespaceHandlers(namespace: string) {
    if (!this.eventHandlers.has(namespace)) {
      this.eventHandlers.set(namespace, new Map());
    }
    return this.eventHandlers.get(namespace)!;
  }

  private getEventHandlers(namespace: string, event: string) {
    const namespaceHandlers = this.getNamespaceHandlers(namespace);
    if (!namespaceHandlers.has(event)) {
      namespaceHandlers.set(event, new Map());
    }
    return namespaceHandlers.get(event)!;
  }

  private clearNamespaceHandlers(namespace: string) {
    this.eventHandlers.delete(namespace);
  }

  connect(namespace: string, options: IConnectOptions) {
    if (this.sockets.has(namespace)) {
      console.log("Socket already connected to namespace:", namespace, options);
      return;
    }

    const url = `${BASE_URL}/${namespace}`;

    const socket = io(url, {
      auth: options.auth,
      query: options?.query,
      timeout: 20000,
      reconnection: true,
      reconnectionAttempts: 5,
    });

    socket.on("connect", () => {
      // Healthy again — allow a fresh recovery attempt on the next disconnect.
      this.authRecoveryAttempted.delete(namespace);
      this.reconnectingNamespaces.delete(namespace);
      this.failedNamespaces.delete(namespace);
      this.syncConnectionStatus();
      console.log(`Connected to namespace "${namespace}" with id: ${socket.id}`);
    });

    socket.on("disconnect", (reason) => {
      console.log(`Disconnected from namespace "${namespace}". Reason: ${reason}`);
    });

    // Manager-level reconnection lifecycle. socket.io retries automatically
    // (reconnectionAttempts: 5); we mirror that into app-wide status so the UI
    // can show a calm "Reconnecting…" while it retries, and a terminal
    // "Connection lost" only once it actually gives up.
    socket.io.on("reconnect_attempt", () => {
      this.reconnectingNamespaces.add(namespace);
      this.syncConnectionStatus();
    });

    socket.io.on("reconnect_failed", () => {
      this.reconnectingNamespaces.delete(namespace);
      this.failedNamespaces.add(namespace);
      this.syncConnectionStatus();
    });

    socket.on("connect_error", async (err: any) => {
      console.error("Socket connect_error:", err);

      const message = err?.message || err?.data?.message || err?.description || "";
      const lower = message.toLowerCase();
      const isAuthError =
        lower.includes("unauthorized") ||
        lower.includes("session expired") ||
        lower.includes("invalid token") ||
        lower.includes("expired access token") ||
        lower.includes("access token missing") ||
        lower.includes("invalid or expired");

      if (!isAuthError) {
        // Transient/connectivity error — let socket.io's reconnection machinery
        // retry. The connection-status bar reflects it via the reconnect_attempt
        // / reconnect_failed lifecycle above; no toast spam here.
        return;
      }

      // Auth error. Recover AT MOST ONCE per disconnect cycle (flag cleared on
      // a successful "connect"). Makes a refresh→reconnect loop impossible.
      if (this.authRecoveryAttempted.has(namespace)) {
        this.forceLogout();
        return;
      }
      this.authRecoveryAttempted.add(namespace);

      // Cookie token may just be expired while the refresh token is still valid
      // (tab woke from sleep). Refresh once, then reconnect with the rotated
      // token (auth provider reads it fresh).
      const result = await silentRefresh(); // shared app-wide mutex
      if (result.ok) {
        socket.connect();
      } else if (result.reason === "network") {
        // Refresh never reached the BE — the session isn't proven dead, so leave
        // socket.io's reconnection machinery to keep trying instead of nuking
        // the user's tokens.
        this.authRecoveryAttempted.delete(namespace);
      } else {
        this.forceLogout();
      }
    });

    this.sockets.set(namespace, socket);
  }

  private forceLogout() {
    clearAuthTokens();
    store.dispatch({ type: LOGOUT_USER_ACTION.NAME });
    // Kill every socket — a dead session invalidates all namespaces, and this
    // stops the other namespace from cascading its own failed-refresh attempt.
    this.disconnectAll();
    // Shared toast id → only one "session expired" even if multiple sockets fail.
    toast.error("Session expired. Please sign in again.", { id: "session-expired" });
  }

  // Manual retry for the "Connection lost" UI. Forces socket.io to start a fresh
  // reconnection cycle on every live socket (it has stopped after exhausting its
  // attempts), and optimistically flips status back to "reconnecting".
  reconnectAll() {
    this.sockets.forEach((socket, namespace) => {
      if (!socket.connected) {
        this.failedNamespaces.delete(namespace);
        this.reconnectingNamespaces.add(namespace);
        socket.connect();
      }
    });
    this.syncConnectionStatus();
  }

  disconnect(namespace: string) {
    const socket = this.sockets.get(namespace);
    if (socket) {
      socket.disconnect();
      this.sockets.delete(namespace);
    }
    this.clearNamespaceHandlers(namespace);
    this.authRecoveryAttempted.delete(namespace);
    // Intentional teardown — drop this namespace from the down-sets so it never
    // leaves a stale "Reconnecting…"/"Connection lost" bar on screen.
    this.reconnectingNamespaces.delete(namespace);
    this.failedNamespaces.delete(namespace);
    this.syncConnectionStatus();
  }

  disconnectAll() {
    this.sockets.forEach((socket) => socket.disconnect());
    this.sockets.clear();
    this.eventHandlers.clear();
    this.authRecoveryAttempted.clear();
    // Clear status — a full teardown (logout/unmount) is not a connection fault.
    this.reconnectingNamespaces.clear();
    this.failedNamespaces.clear();
    this.syncConnectionStatus();
  }

  isConnected(namespace: string) {
    return this.sockets.get(namespace)?.connected || false;
  }

  onConnect(namespace: string, callback: () => void) {
    const socket = this.sockets.get(namespace);
    if (!socket) {
      console.warn(`Socket for namespace "${namespace}" not found`);
      return;
    }

    if (socket.connected) {
      callback();
    }

    socket.on("connect", callback);
  }

  offConnect(namespace: string, callback: () => void) {
    const socket = this.sockets.get(namespace);
    if (!socket) return;
    socket.off("connect", callback);
  }

  emit(namespace: SOCKET_NAME_SPACES, event: string, data: any) {
    const socket = this.sockets.get(namespace);
    if (socket?.connected) {
      socket.emit(event, data);
    }
  }

  /**
   * Emit and wait for the server's acknowledgement. Resolves null when the
   * socket is down or the server doesn't answer in time — callers treat the
   * ack as best-effort and retry on the next connect.
   */
  emitWithAck<T>(
    namespace: SOCKET_NAME_SPACES,
    event: string,
    data: unknown,
    timeoutMs = 8000,
  ): Promise<T | null> {
    const socket = this.sockets.get(namespace);
    if (!socket?.connected) return Promise.resolve(null);
    return socket
      .timeout(timeoutMs)
      .emitWithAck(event, data)
      .then((ack: T) => ack)
      .catch(() => null);
  }

  on<T>(
    namespace: string,
    event: string,
    callback: (payload: T, event: string) => void,
  ): void {
    const socket = this.sockets.get(namespace);
    if (!socket) {
      console.warn(`Socket for namespace "${namespace}" is not connected`);
      return;
    }

    const eventHandlers = this.getEventHandlers(namespace, event);

    // Caller's callback as a stable identity key. socket.io payloads are
    // untyped at runtime, so this single cast is the typed/untyped boundary.
    const key = callback as unknown as SocketEventHandler;

    const existing = eventHandlers.get(key);
    if (existing) socket.off(event, existing);

    // Wrapper matches socket.io's (...args: unknown[]) signature; we own the
    // payload typing via the generic T.
    const wrapped: SocketEventHandler = (...args) => {
      callback(args[0] as T, event);
    };

    eventHandlers.set(key, wrapped);
    socket.on(event, wrapped);
  }

  off<T>(
    namespace: SOCKET_NAME_SPACES,
    event: string,
    callback: (payload: T) => void,
  ): void {
    const socket = this.sockets.get(namespace);
    if (!socket) return;

    const key = callback as unknown as SocketEventHandler;
    const wrapped = this.eventHandlers.get(namespace)?.get(event)?.get(key);

    socket.off(event, wrapped ?? key);

    if (wrapped) {
      const eventHandlers = this.eventHandlers.get(namespace)?.get(event);
      eventHandlers?.delete(key);
      if (eventHandlers?.size === 0) {
        this.eventHandlers.get(namespace)?.delete(event);
      }
      if (this.eventHandlers.get(namespace)?.size === 0) {
        this.eventHandlers.delete(namespace);
      }
    }
  }
}

export const socketManager = new SocketManager();
